import { MouseEvent, FC, Dispatch, SetStateAction, ChangeEvent } from 'react';
import { useMemo } from 'react';
import {
  Box,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { TaskRo } from 'src/app/services/api.generated';

type Order = 'asc' | 'desc';

type HeadCell = {
  disablePadding: boolean;
  id: keyof TaskRo;
  label: string;
  numeric: boolean;
};

type TasksTableHeaderPropsType = {
  onRequestSort: (event: MouseEvent<unknown>, property: keyof TaskRo) => void;
  order: Order;
  orderBy: string;
  selected: string[];
  setSelected: Dispatch<SetStateAction<string[]>>;
  rows: TaskRo[];
  showCheckbox: boolean;
};

export const TasksTableHeader: FC<TasksTableHeaderPropsType> = ({
  onRequestSort,
  order,
  orderBy,
  selected,
  setSelected,
  rows,
  showCheckbox,
}) => {
  const { t } = useTranslation();
  const createSortHandler =
    (property: keyof TaskRo) => (event: MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const headCells: readonly HeadCell[] = [
    {
      id: 'name',
      numeric: false,
      disablePadding: true,
      label: t('name'),
    },
    {
      id: 'creator',
      numeric: false,
      disablePadding: false,
      label: t('creator'),
    },
    {
      id: 'assigneeId',
      numeric: false,
      disablePadding: false,
      label: t('assignee'),
    },
    {
      id: 'createdAt',
      numeric: false,
      disablePadding: false,
      label: t('createdAt'),
    },
    {
      id: 'state',
      numeric: false,
      disablePadding: false,
      label: t('status'),
    },
    {
      id: 'outputState',
      numeric: false,
      disablePadding: false,
      label: t('Generated'),
    },
  ];

  const selectedRowsCount = useMemo(() => selected.length, [selected.length]);
  const totalRowsCount = useMemo(() => rows.length, [rows.length]);

  const checkBoxOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map(({ id }) => id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  return (
    <TableHead>
      <TableRow
        sx={{
          '& > th': {
            py: 0.75,
            px: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
            color: 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
          },
        }}
      >
        {showCheckbox && (
          <TableCell padding="checkbox">
            <Checkbox
              size="small"
              color="primary"
              indeterminate={
                selectedRowsCount > 0 && selectedRowsCount < totalRowsCount
              }
              checked={
                totalRowsCount > 0 && selectedRowsCount === totalRowsCount
              }
              onChange={checkBoxOnChange}
              inputProps={{
                'aria-label': String(t('selectAll')),
              }}
            />
          </TableCell>
        )}
        {headCells.map((headCell) => (
          <TableCell
            align="left"
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              sx={{
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                  borderRadius: 0.5,
                },
              }}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell align="right">
          <Box component="span" sx={visuallyHidden}>
            {t('taskTable.actions', { defaultValue: 'Actions' })}
          </Box>
        </TableCell>
      </TableRow>
    </TableHead>
  );
};
